import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
  companyId: string;
}

export interface LoginPayload {
  email: string;
  password: string;
  regid: null;
  language: string;
}

export interface LoginResponseData {
  id: string;
  userName: string;
  email: string;
  token: string;
  roles: string;
  company: {
    id: string;
    nm_company: string;
  };
}

export interface LoginResponse {
  data: LoginResponseData;
}

interface JwtPayload {
  exp: number;
  iat: number;
  sub: string;
  'https://hasura.io/jwt/claims'?: {
    'x-hasura-user-id': string;
    'x-hasura-company-id': string;
    'x-hasura-default-role': string;
    'x-hasura-allowed-roles': string[];
  };
  [key: string]: unknown;
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'm2_access_token',
  USER: 'm2_user',
} as const;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly userSubject = new BehaviorSubject<AuthUser | null>(this.getStoredUser());
  readonly user$ = this.userSubject.asObservable();

  login(email: string, password: string): Observable<LoginResponse> {
    const payload: LoginPayload = { email, password, regid: null, language: '' };

    return this.http
      .post<LoginResponse>(environment.loginUrl, payload)
      .pipe(
        tap((response) => {
          const d = response.data;
          if (d?.token) {
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, d.token);

            const claims = this.getHasuraClaims(d.token);
            const user: AuthUser = {
              id: claims?.['x-hasura-user-id'] ?? d.id,
              name: d.userName,
              email: d.email,
              role: d.roles,
              companyId: claims?.['x-hasura-company-id'] ?? d.company?.id ?? '',
            };

            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
            this.userSubject.next(user);
          }
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  logout(): void {
    this.clearStorage();
    this.userSubject.next(null);
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = this.decodeJwt(token);
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getUser(): AuthUser | null {
    return this.userSubject.value;
  }

  /** Papéis permitidos no token (x-hasura-allowed-roles). Vazio se não autenticado. */
  getAllowedRoles(): string[] {
    const token = this.getToken();
    if (!token) return [];
    return this.getHasuraClaims(token)?.['x-hasura-allowed-roles'] ?? [];
  }

  /** true se o usuário pode administrar a rede (controlar o compartilhamento da biblioteca). */
  hasRole(role: string): boolean {
    return this.getAllowedRoles().includes(role);
  }

  private getHasuraClaims(token: string): JwtPayload['https://hasura.io/jwt/claims'] | null {
    try {
      const payload = this.decodeJwt(token);
      return payload['https://hasura.io/jwt/claims'] ?? null;
    } catch {
      return null;
    }
  }

  private getStoredUser(): AuthUser | null {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr) as AuthUser;
    } catch {
      return null;
    }
  }

  private clearStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  private decodeJwt(token: string): JwtPayload {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT token');
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JwtPayload;
  }
}
